import { useState, useEffect, useRef, type ComponentType } from 'react'

/**
 * Props for the LazyOnViewport component.
 * @param importFn - Async function that dynamically imports the target component.
 * @param fallback - React node rendered while the component is loading.
 */
interface LazyOnViewportProps {
  /** Function that returns a promise resolving to the component module */
  importFn: () => Promise<{ default: ComponentType<Record<string, unknown>> }>
  /** Placeholder to show while loading */
  fallback: React.ReactNode
}

/**
 * Lazy-loads a component only when it scrolls near the viewport.
 * Uses IntersectionObserver with rootMargin to trigger the import
 * before the user actually reaches the section.
 */
/**
 * LazyOnViewport - Lazy-loads a component only when it approaches the viewport.
 * Uses IntersectionObserver with a 300px rootMargin so the import fires before
 * the user scrolls to the section, reducing perceived loading time.
 */
const LazyOnViewport = ({ importFn, fallback }: LazyOnViewportProps) => {
  const [Component, setComponent] = useState<ComponentType<Record<string, unknown>> | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  // Stores importFn in a ref to avoid stale closures in the IntersectionObserver
  // callback. Without this, the observer captures the INITIAL importFn and never
  // sees updates (e.g., if section config changes while the page is open).
  const importFnRef = useRef(importFn)

  // Set up IntersectionObserver; triggers the dynamic import when the
  // placeholder enters the 300px rootMargin zone. Disconnects after first
  // intersection to avoid redundant imports.
  useEffect(() => {
    importFnRef.current = importFn

    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting) {
          observer.disconnect()
          importFnRef.current()
            .then((mod) => setComponent(() => mod.default))
            .catch((err) => setError(err))
        }
      },
      {
        // 300px rootMargin (~2-3 average screen inches) starts importing the
        // component before the user scrolls to the section. This gives the
        // dynamic import time to resolve. If sections show shimmer for too
        // long, increase this value. If too many sections load unnecessarily
        // (e.g., on fast scroll-through), decrease it.
        rootMargin: '300px 0px',
        threshold: 0,
      }
    )

    observer.observe(el)

    return () => observer.disconnect()
  }, [importFn])

  // Error state: shows a failure message with a Retry button.
  // KNOWN BUG: The retry button clears the error but does NOT re-observe.
  // The IntersectionObserver was disconnected at the isIntersecting check
  // when it first fired. After clearing the error, Component is still null
  // and the observer is gone, so the fallback shimmer shows forever. The
  // user must refresh the page. Fix: re-create the observer in the error
  // handler or re-observe on retry.
  if (error) {
    return (
      <div ref={ref} className="section-placeholder" style={{ minHeight: '30vh' }}>
        <div className="container" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <p style={{ color: 'var(--text-secondary)' }}>
            Failed to load section. <button onClick={() => setError(null)} className="btn-primary" style={{ padding: '0.3rem 0.8rem', fontSize: '0.8rem' }}>Retry</button>
          </p>
        </div>
      </div>
    )
  }

  // Loading / ready state: renders the fallback placeholder until the
  // dynamic import resolves, then swaps in the full component.
  return (
    <div ref={ref}>
      {Component ? <Component /> : fallback}
    </div>
  )
}

export default LazyOnViewport
